/* Simulated interface activity only; no analytics, orders, or network requests. */
(() => {
  // Morocco-time visitor curve, shared with homepage-live-activity.js. Keep both copies identical so
  // the homepage total always equals the sum of the product-page counts at the same moment.
  // [start minute, end minute, lowest count, highest count] in Africa/Casablanca time.
  const MOROCCO_SLOTS = [
    [0, 180, 10, 15],
    [180, 300, 8, 9],
    [300, 540, 5, 6],
    [540, 840, 15, 20],
    [840, 1080, 25, 35],
    [1080, 1200, 40, 65],
    [1200, 1380, 70, 80],
    [1380, 1440, 40, 70],
  ];
  const moroccoMinutes = (date) => {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Africa/Casablanca',
        hourCycle: 'h23',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      }).formatToParts(date);
      const part = (type) => Number(parts.find((entry) => entry.type === type)?.value);
      const minutes = (part('hour') % 24) * 60 + part('minute') + (part('second') + date.getMilliseconds() / 1000) / 60;
      if (Number.isFinite(minutes)) return minutes;
    } catch {}
    // Fallback to Morocco's standard UTC+1 offset when the time zone is unavailable.
    return ((date.getUTCHours() + 1) % 24) * 60 + date.getUTCMinutes() + (date.getUTCSeconds() + date.getUTCMilliseconds() / 1000) / 60;
  };
  const moroccoSlot = (minutes) =>
    Math.max(0, MOROCCO_SLOTS.findIndex(([start, end]) => minutes >= start && minutes < end));
  const hash = (seed) => {
    let h = Math.imul(seed ^ 0x5bd1e995, 0x27d4eb2d);
    h ^= h >>> 15;
    h = Math.imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    return (h >>> 0) / 4294967296;
  };
  // Deterministic smooth noise in [0, 1): identical for every visitor at the same instant.
  const smoothNoise = (minutes, span, channel) => {
    const position = minutes / span;
    const knot = Math.floor(position);
    const t = position - knot;
    const from = hash(knot * 31 + channel);
    return from + (hash((knot + 1) * 31 + channel) - from) * t * t * (3 - 2 * t);
  };
  // Closest count a slot shares with a neighbouring slot, so boundary jumps stay as small as possible.
  const meetingPoint = (low, high, otherLow, otherHigh) =>
    otherHigh < low ? low : otherLow > high ? high : Math.round((Math.max(low, otherLow) + Math.min(high, otherHigh)) / 2);
  const moroccoVisitorTotal = (date) => {
    const minutes = moroccoMinutes(date);
    const index = moroccoSlot(minutes);
    const [start, end, low, high] = MOROCCO_SLOTS[index];
    const [, , previousLow, previousHigh] = MOROCCO_SLOTS[(index + MOROCCO_SLOTS.length - 1) % MOROCCO_SLOTS.length];
    const [, , nextLow, nextHigh] = MOROCCO_SLOTS[(index + 1) % MOROCCO_SLOTS.length];
    const ramp = Math.min(30, (end - start) / 4);
    let value = low + smoothNoise(date.getTime() / 60000, 4, 1) * (high - low);
    value += (meetingPoint(low, high, previousLow, previousHigh) - value) * Math.max(0, 1 - (minutes - start) / ramp);
    value += (meetingPoint(low, high, nextLow, nextHigh) - value) * Math.max(0, 1 - (end - minutes) / ramp);
    return Math.min(high, Math.max(low, Math.round(value)));
  };
  const msUntilNextSlot = (date) => {
    const minutes = moroccoMinutes(date);
    return Math.max(0, (MOROCCO_SLOTS[moroccoSlot(minutes)][1] - minutes) * 60000) + 250;
  };

  // Split the homepage total across product pages: collagen 40% and magnesium 30%, rounded half up;
  // whatever remains (≈30%) goes to the other four, so the shares always sum exactly to the total.
  // Ranked best to least: their base weights keep this order, while a ±4% drift varies the split naturally.
  const OTHER_PRODUCT_WEIGHTS = { 'daily-vita': 1.15, ashwagandha: 1.05, propolis: 0.95, 'slim-detox': 0.85 };
  const PRODUCT_ALIASES = { 'collagene-marin-max': 'collagene', 'magnesium-bisglycinate': 'magnesium' };
  const productShare = (total, productKey, date) => {
    // Integer arithmetic avoids floating-point errors on exact .5 values.
    const collagen = Math.floor((total * 4 + 5) / 10);
    const magnesium = Math.floor((total * 3 + 5) / 10);
    const key = PRODUCT_ALIASES[productKey] || productKey;
    if (key === 'collagene') return collagen;
    if (key === 'magnesium') return magnesium;
    const keys = Object.keys(OTHER_PRODUCT_WEIGHTS);
    const index = keys.indexOf(key);
    const rest = Math.max(0, total - collagen - magnesium);
    if (index === -1) return Math.round(rest / keys.length);
    const weights = keys.map((name, i) => OTHER_PRODUCT_WEIGHTS[name] * (0.96 + 0.08 * smoothNoise(date.getTime() / 60000, 10, 11 + i)));
    const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
    const exact = weights.map((weight) => (rest * weight) / weightSum);
    const shares = exact.map(Math.floor);
    const remaining = rest - shares.reduce((sum, share) => sum + share, 0);
    const byRemainder = keys.map((_, i) => i).sort((a, b) => exact[b] - shares[b] - (exact[a] - shares[a]) || a - b);
    for (const i of byRemainder.slice(0, remaining)) shares[i] += 1;
    return shares[index];
  };

  // Notice gaps in seconds by Morocco time; 25–55 s is the original interval.
  const noticeDelay = (randomBetween) => {
    const minutes = moroccoMinutes(new Date());
    let [low, high] = [25, 55];
    if (minutes >= 180 && minutes < 540) [low, high] = [35, 70];
    else if (minutes >= 840 && minutes < 1080) [low, high] = [20, 45];
    else if (minutes >= 1080 && minutes < 1200) [low, high] = [15, 35];
    else if (minutes >= 1200 && minutes < 1380) [low, high] = [10, 25];
    else if (minutes >= 1380) {
      // Ease from the evening peak back to the original interval by midnight.
      const progress = (minutes - 1380) / 60;
      [low, high] = [10 + 15 * progress, 25 + 30 * progress];
    }
    return randomBetween(Math.round(low * 1000), Math.round(high * 1000));
  };

  const widgets = document.querySelectorAll('[data-social-proof]:not([data-social-proof-ready])');

  for (const widget of widgets) {
    widget.dataset.socialProofReady = 'true';
    const count = widget.querySelector('[data-viewer-count]');
    const randomBetween = (low, high) => Math.floor(Math.random() * (high - low + 1)) + low;

    // Show the Morocco-time count, refreshed on the original randomized 8–15 second cadence.
    const updateCount = () => {
      const now = new Date();
      const total = moroccoVisitorTotal(now);
      count.textContent = String(widget.dataset.mode === 'product' ? productShare(total, widget.dataset.productKey, now) : total);
      window.setTimeout(updateCount, Math.min(randomBetween(8000, 15000), msUntilNextSlot(now)));
    };
    updateCount();

    if (widget.dataset.orderNotice !== 'true') continue;
    const notice = widget.querySelector('[data-order-message]');
    if (!notice) continue;

    // Collagen and magnesium keep the Morocco-time rhythm; the other products only show notices
    // from 18:00 to midnight (Morocco time), with a long 90–180 second gap.
    const productKey = PRODUCT_ALIASES[widget.dataset.productKey] || widget.dataset.productKey;
    const frequentNotices = productKey === 'collagene' || productKey === 'magnesium';
    const EVENING_START = 1080;

    const scheduleNotice = () => {
      if (!frequentNotices) {
        const minutes = moroccoMinutes(new Date());
        if (minutes < EVENING_START) {
          window.setTimeout(scheduleNotice, (EVENING_START - minutes) * 60000 + 250);
          return;
        }
      }
      // Wait before the first display, then leave a quiet interval between notices.
      window.setTimeout(() => {
        // Skip a notice whose wait ran past midnight; the next one waits for 18:00 again.
        if (!frequentNotices && moroccoMinutes(new Date()) < EVENING_START) {
          scheduleNotice();
          return;
        }
        notice.hidden = false;
        window.setTimeout(() => {
          notice.hidden = true;
          scheduleNotice();
        }, 4500);
      }, frequentNotices ? noticeDelay(randomBetween) : randomBetween(90000, 180000));
    };
    scheduleNotice();
  }
})();
